import { useCallback, useEffect, useState } from "react";
import { getClientApiKeys } from "../services/clientApiKeys";

/**
 * The client's stored API keys (names + masked values), for pickers and the management screen.
 *
 * @param {object} [options]
 * @param {string|null} [options.clientId] Client public id.
 * @param {boolean} [options.enabled] Skip the request when no client is known yet.
 * @returns {{keys: object[], options: {value: string, label: string}[], loading: boolean,
 *   error: string|null, reload: () => void}}
 */
export const useClientApiKeys = ({ clientId = null, enabled = true } = {}) => {
  const [keys, setKeys] = useState([]);
  const [resolvedClientId, setResolvedClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setKeys([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    getClientApiKeys(clientId ? { clientId } : {})
      .then((result) => {
        if (cancelled) return;

        if (result?.success === false) {
          setKeys([]);
          setResolvedClientId(null);
          setError(result.message || "Could not load the client API keys.");
        } else {
          setKeys(result.keys ?? []);
          setResolvedClientId(result.client_id ?? null);
          setError(null);
        }

        setLoading(false);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setKeys([]);
        setError(loadError?.message || "Could not load the client API keys.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, nonce, enabled]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const options = keys.map((key) => ({
    value: key.name,
    label: key.service ? `${key.name} · ${key.service}` : key.name,
  }));

  return { keys, options, loading, error, reload, clientId: resolvedClientId };
};

export default useClientApiKeys;
